import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { jsonLdScript } from "@/lib/jsonld";
import { webPageScript } from "@/lib/webpage-jsonld";
import { ExternalLink, CalendarClock, AlertTriangle } from "lucide-react";

const TITLE = "আয়কর রিটার্ন দাখিলের শেষ তারিখ ও নিয়ম | Tax Return Deadline BD";
const DESC = "আয়কর রিটার্ন দাখিলের সময়সীমা, দেরিতে জমা দিলে জরিমানা এবং সময় বাড়ানোর আবেদন সম্পর্কে জানুন।";
const URL = "https://onlinetaxvat.com/tax-return-deadline-bd";

export const Route = createFileRoute("/tax-return-deadline-bd")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      webPageScript({ path: "/tax-return-deadline-bd", name: TITLE, description: DESC }),
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "আয়কর রিটার্ন দাখিলের সময়সীমা ও দেরিতে জমা দিলে করণীয়",
        description: DESC,
        inLanguage: "bn",
        mainEntityOfPage: URL,
        author: { "@type": "Organization", name: "Online Tax VAT Bangladesh" },
        publisher: { "@type": "Organization", name: "Online Tax VAT Bangladesh" },
      }),
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SiteShell>
      <article className="container-page max-w-3xl py-14 md:py-20">
        <header className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">
            <CalendarClock className="h-4 w-4" /> Tax Return Deadline BD
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">
            আয়কর রিটার্ন দাখিলের সময়সীমা ও দেরিতে জমা দিলে করণীয়
          </h1>
        </header>

        <section className="glass rounded-3xl border border-border/60 p-6 md:p-8 shadow-card space-y-4 text-[15px] leading-relaxed">
          <p>
            প্রতি কর বছরের জন্য <strong>জাতীয় রাজস্ব বোর্ড (NBR)</strong> রিটার্ন দাখিলের একটি নির্দিষ্ট সময়সীমা নির্ধারণ করে। নির্ধারিত সময়ের মধ্যে রিটার্ন দাখিল না করলে করদাতাকে জরিমানা বা সুদ গুনতে হতে পারে। তবে যৌক্তিক কারণ দেখিয়ে উপ-কর কমিশনারের কাছে সময় বাড়ানোর আবেদনও করা যায়।
          </p>
          <p>
            সময়ের পরেও অনলাইনে সাধারণ পদ্ধতিতে রিটার্ন দাখিল করা সম্ভব। সর্বশেষ ও সঠিক সময়সীমা জানতে NBR-এর ওয়েবসাইট (etaxnbr.gov.bd) বা কল সেন্টারে যোগাযোগ করুন।
          </p>
        </section>

        <section className="mt-8 rounded-2xl border border-amber-400/40 bg-amber-50 dark:bg-amber-500/10 p-6">
          <div className="flex items-start gap-3">
            <span className="h-9 w-9 shrink-0 rounded-full bg-amber-500 text-white flex items-center justify-center">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display font-bold text-lg mb-2">২০২৬-২৭ কর বছরের ডেডলাইন</h2>
              <ul className="text-sm space-y-1.5 leading-relaxed">
                <li>• <strong>ব্যক্তিগত করদাতা (Tax Day):</strong> ৩০ নভেম্বর ২০২৬</li>
                <li>• <strong>কোম্পানি করদাতা:</strong> কর বছর শেষ হওয়ার ৭ মাসের মধ্যে বা ১৫ জানুয়ারি ২০২৭ — যেটি পরে</li>
                <li>• <strong>বিলম্ব সারচার্জ:</strong> প্রতি মাসের জন্য প্রদেয় করের উপর ২% হারে সুদ</li>
                <li>• <strong>ন্যূনতম জরিমানা:</strong> ১,০০০ টাকা (অন্যথায় প্রদেয় করের ১০% পর্যন্ত)</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-3">* সরকারি বিজ্ঞপ্তির মাধ্যমে সময়সীমা বৃদ্ধি হতে পারে। সর্বশেষ তারিখ NBR ওয়েবসাইট থেকে নিশ্চিত করুন।</p>
            </div>
          </div>
        </section>

        <section className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-2xl border border-border/60 bg-card">
          <div>
            <h3 className="font-display font-bold">আরও জানুন</h3>
            <Link to="/knowledge-hub/$slug" params={{ slug: "faq-e-return" }} className="text-primary hover:underline text-sm">
              → e-Return ফাইলিং সচরাচর জিজ্ঞাসা (৪৫টি প্রশ্ন-উত্তর)
            </Link>
          </div>
          <a
            href="https://etaxnbr.gov.bd"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition shadow-md"
          >
            etaxnbr.gov.bd এ যান <ExternalLink className="h-4 w-4" />
          </a>
        </section>
        <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["tax-return-deadline-bd"]} />
      </article>
    </SiteShell>
  );
}
